import _ from 'lodash';

import { THEME } from '../../constants/notification';
import { getUploadOperationClientIdByFileId } from '../../features/operation-panel/operation-groups/upload-operations/upload-operations.selectors';
import { getSelectedOrganizationId } from '../../selectors/organizations/organizations';
import { mediaAssetsService } from '../../transport/index';
import { mediaPlatformProvider } from '../../transport/media-platform-provider';
import {
  FileDescriptor,
  Project,
  ProjectInOrganizationId,
} from '../../types/entities';
import {
  LoadFilesRequestData,
  MediaAsset,
  SingleFileUploadPayload,
  UploadErrorActionPayload,
  UploadFileSuccessPayload,
} from '../../types/media-assets';
import { i18n } from '../../utils/index';
import { normalizeFile } from '../../utils/media-assets/media-assets';
import { getProjectByRoute } from '../projects/projects.selectors';
import { Dispatch, GetState, ThunkAction } from '../redux.types';
import { getFileIdByPath, getMediaAssetsMap } from './files.selectors';
import { ListFilesResponse } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/responses/list-files-response';

export const FILES_ACTIONS = {
  FILE_GROUP_UPLOAD_START: 'MEDIA_ASSETS.FILE.FILE_GROUP_UPLOAD_START',
  FILE_CREATE: 'MEDIA_ASSETS.FILE.CREATE',
  FILE_UPLOAD_PROGRESS: 'MEDIA_ASSETS.FILE.UPLOAD_PROGRESS',
  FILE_UPLOAD_SUCCESS: 'MEDIA_ASSETS.FILE.UPLOAD_SUCCESS',
  FILE_UPLOAD_ERROR: 'MEDIA_ASSETS.FILE.UPLOAD_ERROR',
  FILE_CREATING: 'MEDIA_ASSETS.FILE.CREATING',

  FOLDER_CREATE_START: 'MEDIA_ASSETS.FOLDER.CREATE.START',
  FOLDER_CREATE_SUCCESS: 'MEDIA_ASSETS.FOLDER.CREATE.SUCCESS',
  FOLDER_CREATE_ERROR: 'MEDIA_ASSETS.FOLDER.CREATE.ERROR',

  FILE_DELETE_SUCCESS: 'MEDIA_ASSETS.DELETE.SUCCESS',
  FILE_DELETE_ERROR: 'MEDIA_ASSETS.DELETE.ERROR',

  FILE_ACL_UPDATE_SUCCESS: 'MEDIA_ASSETS_ACTIONS__FILE_ACL_UPDATE_SUCCESS',
  FILE_ACL_UPDATE_ERROR: 'MEDIA_ASSETS_ACTIONS__FILE_ACL_UPDATE_ERROR',
};

export const loadSdk = (organizationId: string, project?: Project) => {
  return mediaPlatformProvider.load(organizationId, project);
};

export const getRequestParams = (state) => ({
  project: getProjectByRoute(state),
  organizationId: getSelectedOrganizationId(state),
});

export const uploadFilesGroupStart = (clientFileIds: string[]) => ({
  type: FILES_ACTIONS.FILE_GROUP_UPLOAD_START,
  payload: {
    groupId: _.uniqueId('operationPanelGroupId_'),
    clientFileIds,
  },
});

export const uploadFileProgress = (payload) => ({
  type: FILES_ACTIONS.FILE_UPLOAD_PROGRESS,
  payload,
});

export const uploadFileSuccess = (payload: UploadFileSuccessPayload) => ({
  type: FILES_ACTIONS.FILE_UPLOAD_SUCCESS,
  payload,
});

export const creatingFileSuccess = (payload: SingleFileUploadPayload) => ({
  type: FILES_ACTIONS.FILE_CREATING,
  payload,
});

export const createFileSuccess = (payload: UploadFileSuccessPayload) => ({
  type: FILES_ACTIONS.FILE_CREATE,
  payload,
});

export const createFileError = (payload: UploadErrorActionPayload) => ({
  type: FILES_ACTIONS.FILE_UPLOAD_ERROR,
  payload,
  notification: {
    message: i18n.t('notifications.media-assets.file.upload.error'),
    theme: THEME.ERROR,
  },
});

const createFolderStart = () => ({
  type: FILES_ACTIONS.FOLDER_CREATE_START,
  payload: undefined,
});

const createFolderSuccess = (folder: MediaAsset) => ({
  type: FILES_ACTIONS.FOLDER_CREATE_SUCCESS,
  payload: folder,
});

const createFolderError = (reason) => ({
  type: FILES_ACTIONS.FOLDER_CREATE_ERROR,
  payload: reason,
  notification: {
    message: i18n.t('notifications.media-assets.folder.create.error'),
    theme: THEME.ERROR,
  },
});

export const createFolder =
  (fullPath: string) =>
  (dispatch: Dispatch, getState: GetState): Promise<void> => {
    dispatch(createFolderStart());
    const state = getState();
    const { project, organizationId } = getRequestParams(state);

    return mediaAssetsService
      .createFolder({ path: fullPath, project, organizationId })
      .then(
        (response: FileDescriptor) => {
          const file = normalizeFile(response);

          dispatch(createFolderSuccess(file));
        },
        (reason) => {
          dispatch(createFolderError(_.get(reason.response, 'data')));
          return Promise.reject(reason);
        },
      );
  };

const deleteFileSuccess = (id: string, clientId?: string) => ({
  type: FILES_ACTIONS.FILE_DELETE_SUCCESS,
  payload: {
    id,
    clientId,
  },
});

const deleteFileError = (payload, isFolder) => ({
  type: FILES_ACTIONS.FILE_DELETE_ERROR,
  payload,
  notification: {
    message: i18n.t(
      isFolder
        ? 'notifications.media-assets.folder.delete.error'
        : 'notifications.media-assets.file.delete.error',
    ),
    theme: THEME.ERROR,
  },
});

const deleteFileInternal =
  (
    projectInOrgId: ProjectInOrganizationId,
    { id, isFolder }: { id: string; isFolder: boolean },
    clientId: string | undefined,
  ) =>
  (dispatch: Dispatch): Promise<void> => {
    return mediaAssetsService.deleteFileById(projectInOrgId, id).then(
      () => {
        dispatch(deleteFileSuccess(id, clientId));
      },
      ({ response }) => {
        dispatch(deleteFileError(_.get(response, 'data'), isFolder));
        return Promise.reject(response);
      },
    );
  };

export const deleteFileById =
  (params: { id: string; isFolder: boolean }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const { project, organizationId } = getRequestParams(state);
    const clientId = getUploadOperationClientIdByFileId(state)(params.id);

    if (!project) {
      return Promise.reject(
        'No selected project, this should normally not ever happen',
      );
    }

    return deleteFileInternal(
      { project, organizationId },
      params,
      clientId,
    )(dispatch);
  };

export const deleteFilesByIds =
  (idsList: string[]): ThunkAction<void> =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const filesMap = getMediaAssetsMap(state);
    const requestParams = getRequestParams(state);
    const { project, organizationId } = requestParams;
    if (!project) {
      return Promise.reject(
        'No selected project, this should normally not ever happen',
      );
    }

    _(filesMap)
      .pick(idsList)
      .values()
      .forEach((file: MediaAsset) => {
        const clientId = getUploadOperationClientIdByFileId(state)(file.id);

        deleteFileInternal(
          { project, organizationId },
          { id: file.id, isFolder: file.isFolder },
          clientId,
        )(dispatch);
      });
  };

export const deleteFileByPath =
  (path: string, isFolder: boolean): ThunkAction<Promise<void>> =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const fileId = getFileIdByPath(state, path);

    return mediaAssetsService
      .deleteFileByPath(getRequestParams(state), path)
      .then(
        () => {
          if (fileId) {
            dispatch(deleteFileSuccess(fileId));
          }
        },
        ({ response }) => {
          dispatch(deleteFileError(_.get(response, 'data'), isFolder));
          return Promise.reject(response);
        },
      );
  };

export const checkIfFolderExist = ({
  path,
  organizationId,
  project,
}: LoadFilesRequestData): Promise<ListFilesResponse> => {
  return mediaAssetsService.getListFiles(
    { path, organizationId, project },
    { nextPageToken: undefined, pageSize: 1, sort: undefined },
  );
};

export const checkIfFileExists = ({
  path,
  organizationId,
  project,
}: LoadFilesRequestData): Promise<FileDescriptor> => {
  return mediaAssetsService.getFileByPath({ path, organizationId, project });
};
